import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, forkJoin, map, Observable, switchMap, throwError } from 'rxjs';
import { Dossier, DossierService } from '../dossier/dossier.service';
import { AuditTrailResponse } from './generated/models/audit-trail-response';
import { CaseStateView } from './generated/models/case-state-view';
import { WorkflowGraph } from './generated/models/workflow-graph';
import { WORKFLOW_ENGINE_GATEWAY } from './workflow-engine-gateway';

export class EngineCaseNotFoundError extends HttpErrorResponse {
  constructor() {
    super({ status: 404, statusText: 'Workflow case not found' });
  }
}

/** One case as the review screen needs it: the engine's lifecycle facts joined with IO's detection data on `engineCaseId`. */
export interface ComposedCase {
  state: CaseStateView;
  audit: AuditTrailResponse;
  graph: WorkflowGraph;
  /** Null when IO holds no Dossier for the case, for example a case opened by another consumer. */
  dossier: Dossier | null;
}

@Injectable({ providedIn: 'root' })
export class CaseCompositionService {
  private _gateway = inject(WORKFLOW_ENGINE_GATEWAY);
  private _dossiers = inject(DossierService);

  compose(caseId: string): Observable<ComposedCase> {
    return this._gateway.getCaseState(caseId).pipe(
      catchError((error: unknown) => throwError(() =>
        error instanceof HttpErrorResponse && error.status === 404 ? new EngineCaseNotFoundError() : error,
      )),
      switchMap(state => forkJoin({
        audit: this._gateway.getAuditTrail(caseId),
        graph: this._gateway.getCaseWorkflow(caseId),
        dossier: this._dossiers.getByEngineCaseId(caseId),
      }).pipe(map(parts => ({ state, ...parts })))),
    );
  }
}
